/**
 * Route guards — redirect based on AuthContext status.
 *
 * Three guards:
 *   <RequireGuest>  — only unauthenticated users (e.g. /login, /register)
 *                     Authenticated users get bounced to /dashboard.
 *
 *   <RequireAuth>   — user must have a session (any status except unauthenticated).
 *                     Redirects to /login preserving the original URL.
 *                     This accepts pre-MFA users — useful for /mfa/verify itself.
 *
 *   <RequireMfa>    — user must be fully authenticated AND MFA-verified.
 *                     Pre-MFA users get sent to /mfa/verify.
 *                     Unauthenticated get sent to /login.
 *
 * All guards render a Splash while auth status is "loading".
 */

import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { useAuth } from "./context";

// -----------------------------------------------------------
// Splash — shown during initial /me load
// -----------------------------------------------------------

export function Splash({ hint }: { hint?: string } = {}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-base">
      <div className="flex flex-col items-center gap-3">
        <div
          className="h-10 w-10 animate-spin rounded-full border-2 border-border border-t-brand-500"
          aria-hidden="true"
        />
        {hint ? (
          <p className="text-caption text-ink-subtle">{hint}</p>
        ) : null}
      </div>
    </div>
  );
}

// -----------------------------------------------------------
// RequireGuest
// -----------------------------------------------------------

export function RequireGuest({ children }: { children: ReactNode }) {
  const { status } = useAuth();

  if (status === "loading") return <Splash />;
  if (status === "authenticated") return <Navigate to="/dashboard" replace />;
  if (status === "needs_mfa") return <Navigate to="/mfa/verify" replace />;

  // unauthenticated | error → render the guest content
  return <>{children}</>;
}

// -----------------------------------------------------------
// RequireAuth
// -----------------------------------------------------------

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") return <Splash />;

  if (status === "unauthenticated" || status === "error") {
    // Preserve the attempted URL so we can return post-login
    return (
      <Navigate
        to="/login"
        state={{ from: `${location.pathname}${location.search}` }}
        replace
      />
    );
  }

  return <>{children}</>;
}

// -----------------------------------------------------------
// RequireMfa
// -----------------------------------------------------------

export function RequireMfa({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") return <Splash />;

  if (status === "unauthenticated" || status === "error") {
    return (
      <Navigate
        to="/login"
        state={{ from: `${location.pathname}${location.search}` }}
        replace
      />
    );
  }

  if (status === "needs_mfa") {
    return <Navigate to="/mfa/verify" replace />;
  }

  return <>{children}</>;
}
